"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { PortableText } from "next-sanity";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import {
  PageHomeContentCommunityCard,
  PageHomeSchema,
} from "@/sanity/pages/PageHomeTemplate";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import { resolveLink } from "@/sanity/globals/CTA";

function ArrowRightIcon() {
  return (
    <svg
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[2.25rem] w-[2.25rem] shrink-0"
    >
      <path
        d="M14.25 9H3.75M9.75 4.5L14.25 9L9.75 13.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CommunityCard({
  card,
  language,
}: {
  card: PageHomeContentCommunityCard;
  language: string;
}) {
  const link = resolveLink(card.link || "/", language);
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  return (
    <div className="flex flex-col items-center gap-[2rem] border border-[rgba(0,0,0,0.1)] bg-white p-[4rem] text-center">
      {card.icon && (
        <div className="relative h-[5rem] w-[5rem] shrink-0">
          <GroqImage
            src={card.icon}
            alt=""
            className="!absolute !inset-0 !h-full !w-full !object-contain"
          />
        </div>
      )}
      {card.body && (
        <div className="richtext flex flex-1 flex-col gap-[2rem] prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[2rem] prose-headings:font-medium prose-headings:leading-[1.3] prose-headings:tracking-[-0.08rem] prose-headings:text-black prose-p:mb-0 prose-p:font-geist prose-p:leading-[1.33] prose-p:tracking-[-0.07rem] prose-p:text-black/70">
          <PortableText value={card.body} components={richTextComponent} />
        </div>
      )}
      {card.link && card.linkLabel && (
        <Link
          href={finalLink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-[1rem] flex items-center gap-[0.625rem] font-geist text-[2rem] font-semibold leading-[1.33] tracking-[-0.035rem] text-black transition-opacity hover:opacity-60"
        >
          {card.linkLabel}
          <ArrowRightIcon />
        </Link>
      )}
    </div>
  );
}

export default function ContentCommunitySection({
  data,
}: {
  data: PageHomeSchema;
}) {
  const cards = data.contentCommunityCards ?? [];

  if (
    !data.contentCommunityChip &&
    !data.contentCommunityTitle &&
    !cards.length
  )
    return null;

  return (
    <section className="w-full bg-white lg:pt-[10rem] pt-[5rem] lg:pb-[18rem] flex flex-col items-center gap-[4rem] lg:gap-[8.38rem]">
      {/* Header */}
      <div className="mx-auto flex w-[min(120rem,calc(100%-4rem))] flex-col items-center gap-[2rem]  text-center">
        {data.contentCommunityChip && (
          <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
            {data.contentCommunityChip}
          </p>
        )}
        {data.contentCommunityTitle && (
          <div className="richtext flex flex-col gap-[2rem]">
            <PortableText
              value={data.contentCommunityTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* Cards grid */}
      {cards.length > 0 && (
        <div className="mx-auto w-[min(147.25rem,calc(100%-4rem))] pb-[10rem]">
          <div className="flex flex-col gap-[2rem] lg:grid lg:grid-cols-4">
            {cards.map((card) => (
              <CommunityCard
                key={card._key}
                card={card}
                language={data.settings?.language || DEFAULT_LANGUAGE}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
